import React, {useState} from 'react';
import {OAuthLink, useOAuthLinkService, OAuthProviderType, OAUTH_LINKS_UPDATED} from 'nekst-api';
import {LoadingAnimation} from 'ui-builder';
import {useEventsSubscriber} from 'event-bus';
import OAuthLinkItem from './OAuthLinkItem';
import ConnectOAuthButton from './ConnectOAuthButton';
import styles from './OAuthLinksWidget.module.scss';
import {AppleSignInToggleWrapper} from "feature-toggle";

export default function OAuthLinksWidget() {
  const [oauthLinks, setOauthLinks] = useState<OAuthLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const oauthLinkService = useOAuthLinkService();

  const loadOAuthLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      const links = await oauthLinkService.getLinks();
      setOauthLinks(links);
    } catch (e) {
      setError('Failed to load connected accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to OAuth links updates
  useEventsSubscriber(
    'OAuthLinksWidget',
    {
      [OAUTH_LINKS_UPDATED]: loadOAuthLinks,
    },
    [],
    loadOAuthLinks,
  );

  const handleDisconnect = async (linkId: number) => {
    try {
      await oauthLinkService.deleteLink(linkId);
      // The list will reload automatically via the event subscription
    } catch (e) {
      setError('Failed to disconnect account. Please try again.');
    }
  };

  const getConnectedProvider = (provider: OAuthProviderType): OAuthLink | undefined => {
    return oauthLinks.find(link => link.provider === provider);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingAnimation />
      </div>
    );
  }

  return (
    <div className={styles.widget}>
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}
      <div className={styles.description}>
        Connect your Google or Apple account for quick and secure sign-in.
      </div>
      {oauthLinks.length === 0 && (
        <div className={styles.emptyState}>
          <p>No connected accounts yet.</p>
          <p>Connect an account below for faster sign-in.</p>
        </div>
      )}
      <div className={styles.providers}>
        {/* Google OAuth */}
        {getConnectedProvider(OAuthProviderType.GOOGLE) ? (
          <OAuthLinkItem
            link={getConnectedProvider(OAuthProviderType.GOOGLE)!}
            onDisconnect={handleDisconnect}
          />
        ) : (
          <ConnectOAuthButton provider={OAuthProviderType.GOOGLE} />
        )}

        <AppleSignInToggleWrapper>
          {/* Apple OAuth */}
          {getConnectedProvider(OAuthProviderType.APPLE) ? (
            <OAuthLinkItem
              link={getConnectedProvider(OAuthProviderType.APPLE)!}
              onDisconnect={handleDisconnect}
            />
          ) : (
            <ConnectOAuthButton provider={OAuthProviderType.APPLE} />
          )}
        </AppleSignInToggleWrapper>

      </div>

    </div>
  );
}
